import React, { useState, useRef, useEffect } from 'react';
import ImageEditor from 'react-avatar-editor'

import { makeStyles  } from '@material-ui/core/styles';

import Slider from '@material-ui/core/Slider';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import CloseIcon from '@material-ui/icons/Close';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';

import VaporExtension from 'app/utils/VaporExtension';
import FileExtensions from 'app/utils/FileExtensions';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    imageCenter: {
        paddingTop: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        textAlign: 'center',
    },
    buttonLeft: {
        paddingRight: theme.spacing(2),
    },
    buttonRight: {
        paddingLeft: theme.spacing(2),
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    wrapper: {
        margin: theme.spacing(1),
        position: 'relative',
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    scaleContainer: {
        maxWidth: 600
    }
}));

const dataURItoBlob = dataURI => {
    // convert base64/URLEncoded data component to raw binary data held in a string
    var byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0)
        byteString = atob(dataURI.split(',')[1]);
    else
        byteString = unescape(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to a typed array
    var ia = new Uint8Array(byteString.length);
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], {type:mimeString});
}

export default function EditImageDialog(props) {

    const { open, serverUrl, onSave, onClose } = props;

    const editorEl = useRef(null);

    const image = props.file ? props.file.full_url : null;
    const dialogSize = props.dialogSize || 'sm';
    const initialScale = props.scale || 50;
    const scaleMin = props.scaleMin || 5;
    const scaleMax = props.scaleMax || 100;
    const initialEditorWidth = props.editorWidth || 400;
    const initialEditorHeight = props.editorHeight || 400;
    const borderRadius = props.borderRadius || 0;
    const imageType = props.imageType || "image/jpeg";

    const [ scale, setScale ] = useState(initialScale)
    const [ rotation, setRotation ] = useState(0)
    const [ editorWidth, setEditorWidth ] = useState(initialEditorWidth)
    const [ editorHeight, setEditorHeight ] = useState(initialEditorHeight)

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});
    const [ imageLoaded, setImageLoaded ] = useState(false);

    const classes = useStyles();
    const api = useApi();

    useEffect(() => {
        const img = new Image();
        img.onload = function() {
            setImageLoaded(true);
        };
        img.src = image;
        img.crossOrigin = "anonymous";
    }, [])

    const handleImageLoaded = imgInfo => {

        if (props.useImageDimensions) {
            setEditorWidth(imgInfo.width);
            setEditorHeight(imgInfo.height);
        } else {
            setEditorWidth(initialEditorWidth);
            setEditorHeight(initialEditorHeight);
        }

        setScale(initialScale);
        setRotation(0);
        setLoading(false);
    }

    const handleSlider = (event, value) => {
        setScale(value);
    };

    const rotateImageLeft = () => {
        let newRotation = rotation - 90;

        if (newRotation < 0) newRotation = 270;

        setRotation(newRotation);
    }

    const rotateImageRight = () => {
        let newRotation = rotation + 90;

        if (newRotation >= 360) newRotation = 0;

        setRotation(newRotation);
    }

    const postSave = () => {
        setLoading(true);

        const canvas = editorEl.current.getImage();
        const dataUrl = canvas.toDataURL(imageType);
        const blobData = dataURItoBlob(dataUrl);
        const token = api.defaults.headers.common['Authorization']

        const img = new File( [blobData], 'tmp-image', { type: imageType } );

        VaporExtension.store(img, {}, token).then(response => {
            api.post(serverUrl, {
                uuid: response.uuid,
                url: response.url,
                key: response.key,
                extension: FileExtensions[imageType],
                bucket: response.bucket,
                content_type: img.type,
                size: img.size,
                id: props.file.id
            }).then(res => {
                onSave(res.file);
            }).finally(() => {
                setLoading(false);
            });

        });
    }

    return (
        <Dialog
            aria-labelledby="edit-image-dialog-title"
            open={open}
            fullWidth
            maxWidth={dialogSize}
            disableBackdropClick={loading}
            disableEscapeKeyDown={loading}
            onClose={onClose}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-image-dialog-title">
                Editing {props.file.name}
                <IconButton
                    aria-label="Cancel"
                    className={classes.closeButton}
                    onClick={onClose}
                    disabled={loading}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <div className={classes.imageCenter}>
                    {imageLoaded &&
                        <ImageEditor
                            image={image}
                            width={editorWidth}
                            height={editorHeight}
                            scale={parseFloat((scale / initialScale).toFixed(1))}
                            rotate={rotation}
                            ref={editorEl}
                            borderRadius={borderRadius}
                            onLoadSuccess={handleImageLoaded}
                            crossOrigin="anonymous"
                        />
                    }
                    <Grid
                        className={classes.scaleContainer}
                        container
                        alignItems="center"
                        justify="center"
                        wrap="nowrap"
                        >
                        <Grid item className={classes.buttonLeft}>
                            <IconButton aria-label="Rotate Left" onClick={rotateImageLeft} color="primary">
                                <RotateLeftIcon />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                            <Slider
                                className={classes.slider}
                                value={scale}
                                min={scaleMin}
                                step={props.scaleMin}
                                max={scaleMax}
                                aria-label="Scale Image"
                                onChange={handleSlider}
                                />
                        </Grid>
                        <Grid item className={classes.buttonRight}>
                            <IconButton aria-label="Rotate Left" onClick={rotateImageRight} color="primary">
                                <RotateRightIcon />
                            </IconButton>
                        </Grid>
                        {/*}
                            <Grid item>
                                <IconButton aria-label="Remove Image" onClick={removeImage} color="primary">
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                            */}
                    </Grid>
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <div className={classes.wrapper}>
                    <Button
                        variant="contained"
                        color="primary"
                        aria-label="Save Image"
                        disabled={loading}
                        className={classes.button}
                        onClick={postSave}
                        >
                        Save
                    </Button>
                    {loading  &&
                        <CircularProgress
                            size={24}
                            className={classes.buttonProgress}
                            color="primary"
                        />
                    }
                </div>
            </DialogActions>
        </Dialog>
    )
}
